import { Button, TextCaption } from '@pypestream/design-system';
import { FC } from 'react';
import { useNavigate } from 'react-router';

import { useProStudioViewerRole } from '../hooks';
import { useProStudioCtxSelector } from '../xstate/app.xstate';
import { setPendingSetupCopy } from './setup-copy';

/**
 * The setup a solution was made from (prototype's Project settings "Setup"):
 * the answers its version keeps — model, what it calls itself, brand, tone,
 * documents and tasks — and "Copy as a new solution", which opens Start's
 * step-by-step form with them filled in. Regenerating this solution in place
 * has no backend yet, so it isn't offered.
 */
export const SolutionSetup: FC = () => {
  const navigate = useNavigate();
  const isViewer = useProStudioViewerRole();
  const { projectId, solutionName, version } = useProStudioCtxSelector(
    (ctx) => ({
      projectId: ctx.project?.id,
      solutionName: ctx.selectedBot?.displayName,
      version: ctx.draftVersionSolutionData,
    })
  );
  if (!version) return null;

  const tasks = (version.tasks ?? []).filter((t) => t?.name?.trim());
  const docs = (version.files ?? [])
    .map((f) => f?.originalName)
    .filter((n): n is string => !!n);
  const model = version.llmCredential?.model || version.llmCredential?.name;
  const hasSetup =
    tasks.length > 0 ||
    !!version.supervisorName?.trim() ||
    !!version.brandName?.trim() ||
    !!version.tone?.trim();

  const rows: [string, string][] = [
    ['Model', model || '—'],
    ['Solution name', version.supervisorName?.trim() || '—'],
    ['Brand name', version.brandName?.trim() || '—'],
    ['Tone', version.tone?.trim() || '—'],
    ['Documents', docs.length ? docs.join(', ') : 'None'],
    ['Tasks', tasks.length ? tasks.map((t) => t.name).join(', ') : 'None'],
  ];

  const copy = () => {
    if (!projectId) return;
    setPendingSetupCopy({
      title: solutionName ? `${solutionName} copy` : '',
      intro: version.supervisorName ?? '',
      brand: version.brandName ?? '',
      tone: version.tone ?? '',
      tasks: tasks.map((t) => ({
        name: t.name ?? '',
        phrases: t.description ?? '',
      })),
    });
    navigate(`/${projectId}`);
  };

  return (
    <section className="c-solution-setup" data-testid="bobcat-solution-setup">
      <span className="c-solution-overview__eyebrow">Setup</span>
      {hasSetup ? (
        <>
          <dl className="c-solution-setup__answers">
            {rows.map(([k, v]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
          {!isViewer && (
            <Button
              size="small"
              variant="secondary"
              test-id="bobcat-solution-setup-copy"
              onClick={copy}
            >
              Copy as a new solution
            </Button>
          )}
        </>
      ) : (
        <TextCaption variant="secondary">
          This solution started blank, so there’s no setup to copy.
        </TextCaption>
      )}
    </section>
  );
};
